// src/components/course/manager/LessonManagement.jsx
import React from 'react';
import { 
  Plus, Play, FileText, HelpCircle, BookOpen, Edit3, Eye, Clock,
  GripVertical, Trash2, Target
} from 'lucide-react';
import { motion } from 'framer-motion';
import { courseService } from '../../../services/courseService';

const LessonManagement = ({ 
  lessons, 
  bookId,
  courseId, 
  viewMode, 
  onLessonSelect, 
  selectedLesson,
  onLessonsUpdate,
  onNavigate
}) => {
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return FileText;
      case 'quiz': return HelpCircle;
      default: return BookOpen;
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    const lessonToDelete = lessons.find(lesson => lesson.id === lessonId);
    const confirmMessage = `Are you sure you want to delete "${lessonToDelete?.title}"?\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Delete the lesson from Firestore
      await courseService.deleteLesson(bookId, courseId, lessonId);
      
      // Update the lessons list and reorder
      const updatedLessons = lessons
        .filter(lesson => lesson.id !== lessonId)
        .map((lesson, index) => ({ ...lesson, order: index }));
      
      // Update lesson order in Firestore if needed
      if (updatedLessons.length > 0) {
        await courseService.reorderLessons(bookId, courseId, updatedLessons.map(l => l.id));
      }
      
      // Update parent component state
      if (onLessonsUpdate) {
        onLessonsUpdate(updatedLessons);
      }
      
      // Clear selected lesson if it was the deleted one
      if (selectedLesson?.id === lessonId) {
        onLessonSelect(null);
      }
      
      alert(`"${lessonToDelete?.title}" deleted successfully`);
      
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson. Please try again.');
    }
  };

  if (viewMode === 'learner') {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Course Content</h3>
          
          {lessons.length > 0 ? (
            <div className="space-y-3">
              {lessons.map((lesson, index) => {
                const Icon = getLessonIcon(lesson.type);
                return (
                  <motion.div
                    key={lesson.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedLesson?.id === lesson.id ? 'ring-2 ring-blue-500 border-blue-500' : ''
                    }`}
                    onClick={() => onLessonSelect(lesson)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon className="w-4 h-4 text-gray-400" />
                          <h4 className="font-medium text-gray-900">{lesson.title}</h4>
                          {lesson.isPreview && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              Preview
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDuration(lesson.duration || 0)}</span>
                          </div>
                          <span className="capitalize">{lesson.type}</span>
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        <Eye className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No lessons available</h4>
              <p className="text-gray-500">This course doesn't have any lessons yet</p>
            </div>
          )}
        </div>

        {/* Selected Lesson Detail */}
        {selectedLesson && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lesson Preview</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{selectedLesson.title}</h4>
                <p className="text-gray-600">{selectedLesson.description || 'No description available'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium capitalize">{selectedLesson.type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="font-medium">{formatDuration(selectedLesson.duration || 0)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium">{selectedLesson.isPreview ? 'Preview Available' : 'Premium Only'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Creator view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Course Lessons</h3>
        <button
          onClick={() => onNavigate('lesson-create', { courseId })}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Lesson</span>
        </button>
      </div>

      {lessons.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-200">
            {lessons.map((lesson, index) => {
              const Icon = getLessonIcon(lesson.type);
              return (
                <motion.div
                  key={lesson.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                      
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-medium text-gray-900">
                            {index + 1}. {lesson.title}
                          </h4>
                          {lesson.isPreview && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              Preview
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="capitalize">{lesson.type}</span>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDuration(lesson.duration || 0)}</span>
                          </div>
                          {lesson.description && (
                            <span className="truncate max-w-xs">{lesson.description}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onNavigate('lesson', { courseId, lessonId: lesson.id })}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="View lesson details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onNavigate('creator-lesson-edit', { courseId, lessonId: lesson.id })}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit lesson"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onNavigate('creator-lesson-interactive', { courseId, lessonId: lesson.id })}
                        className="text-orange-400 hover:text-orange-600 transition-colors"
                        title="Add interactions"
                      >
                        <Target className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteLesson(lesson.id)}
                        className="text-red-400 hover:text-red-600 transition-colors"
                        title="Delete lesson"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No lessons yet</h4>
          <p className="text-gray-600 mb-6">Start building your course by adding your first lesson</p>
          <button
            onClick={() => onNavigate('lesson-create', { courseId })}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create First Lesson</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default LessonManagement;